/**
 * THE AUTH SEAM. **THERE IS STILL NO AUTHENTICATION IN THIS FILE — IT IS IN `src/auth/`.**
 * ======================================================================================
 *
 * ⚠️ **UPDATE, and read this before the historical note below it.** The real provider now exists:
 * `src/auth/oidc.ts` implements {@link TokenProvider} as OIDC Authorization Code + PKCE, and
 * `src/auth/install.ts` installs it through {@link setTokenProvider}. **The seam below is unchanged in
 * shape** — same `kind`, same `getAccessToken()`, same three functions — because
 * `test/compose/kiosk-lookup.test.ts` and `test/sync-retry.test.ts` install providers through it.
 *
 * What has NOT changed, and must not:
 *
 *  · **{@link devStubTokenProvider} stays, and `api.ts` still refuses to send when it is active.** That
 *    is not legacy scaffolding; it is the state a build with no configured app client id runs in. See
 *    `src/auth/install.ts`: when the configuration is incomplete, `setTokenProvider` is **not called**,
 *    the stub remains, and the app sends nothing rather than firing requests that would all come back
 *    401 and read like an expired session.
 *  · **The four requirements at the bottom of this comment are all met** by `src/auth/oidc.ts`, and it
 *    says where. Requirement 4 in particular — a failed refresh must not drop queued work — is the
 *    reason `src/auth/` contains no import of `outbox.ts` at all.
 *
 * ⚠️ **NO LIVE SIGN-IN HAS BEEN PERFORMED.** There is no app client id on the pool yet, and which
 * issuer/JWKS applies is itself unresolved (see `src/auth/config.ts`). Every assertion in `test/auth/**`
 * runs against an injected `fetch`.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 * THE ORIGINAL NOTE, kept because its reasoning is still the reason the stub behaves as it does.
 * ─────────────────────────────────────────────────────────────────────────────────────────────────
 *
 * This module defines the interface a real token provider must satisfy and ships a stub that
 * returns no token, ever. It is scaffolding for someone else's work, and it is written so that it
 * cannot be mistaken for the real thing:
 *
 *  · `devStubTokenProvider.getAccessToken()` resolves to `null` — there is no code path here that
 *    produces a string that looks like a bearer token, not even in a test fixture;
 *  · `kind` is the literal `'stub'`, and `api.ts` branches on it to return an explicit
 *    `auth-not-configured` outcome **instead of sending an unauthenticated request**. So a screen
 *    built against this seam shows "sign-in not built" rather than a 401 that a reader might
 *    mistake for an expired session;
 *  · it logs a warning once per session, so a stub left in a deployed bundle is visible in a
 *    console rather than inferred from behaviour.
 *
 * WHAT THE REAL IMPLEMENTATION IS, AND WHY IT IS NOT HERE.
 * -------------------------------------------------------
 * OIDC Authorization Code + PKCE against IdPrism via Federate. What is known from
 * `discovery/auth/idprism.md` and `docs/architecture/02-identity-and-authorization.md` as read on
 * 2026-09-03:
 *
 *  · user pool `us-east-1_4MmYZszEG`, tenant `f5cfac60-ef1d-4972-854e-8a2917b199fd`, shared with
 *    River External and Fresnel — so **this app's configuration is not the only consumer of that
 *    tenant**, and a change made for us can affect two other products;
 *  · `scopes_supported` is `["openid"]` only. Claims come from the service profile's **Claim
 *    Configuration** tab, not from scopes;
 *  · **the Federate service profile for this app does not exist yet (OQ-5).** The API authorizer
 *    therefore denies 100% of requests today with `configuration-incomplete`. There is nothing for
 *    a PKCE flow to talk to, which is why building one now would produce something that fails in a
 *    way indistinguishable from a bug;
 *  · `custom:supplierId` must be in that Claim Configuration or the authorizer cannot resolve a
 *    supplier scope, and `email` must be there too or C-12 technician attribution stays blocked and
 *    `POST /submissions` keeps answering 503 (see `infra/src/handlers/submission-intake.ts`).
 *
 * I have not verified any of that against a live Federate console — it is read out of this repo's
 * own documents. Treat the identifiers as sourced-from-docs, not confirmed.
 *
 * REQUIREMENTS THE REAL PROVIDER MUST MEET, recorded here because they are the parts that get lost.
 * **All four are now implemented; each line says where, so a reader can check rather than trust.**
 *
 *  1. **Tokens never in `localStorage`.** In memory, with the refresh token in an HttpOnly cookie or
 *     nowhere at all. A bearer token in `localStorage` is readable by any XSS on the origin, and this
 *     origin also serves the photo redirect.
 *     → `src/auth/oidc.ts`, the `#tokens` private field: nowhere else, never serialised, dies with the
 *       tab. `src/auth/transient.ts` documents the ONE thing written to `sessionStorage` — the
 *       single-use PKCE `code_verifier`, which is not a token — and why. **"Nowhere at all" is the
 *       option taken**, so relaunching from the home screen means signing in again; that cost is stated
 *       to the technician in `src/auth/phase.ts` rather than paid for with a stealable refresh token.
 *  2. **PKCE `S256` only**, `code_challenge_method=plain` refused, `state` and `nonce` verified.
 *     → `src/auth/pkce.ts` (base64url, not base64 — the classic silent failure) and
 *       `src/auth/oidc.ts#handleRedirect`, where a mismatched `state` **aborts** rather than warning.
 *  3. **The refresh must be single-flight.** Twelve queued submissions draining at once must not
 *     fire twelve refreshes; `getAccessToken()` has to coalesce. That is why the interface is a
 *     method on a provider object rather than a bare function the caller can copy around.
 *     → `src/auth/oidc.ts`, `#refreshSingleFlight`.
 *  4. **A failed refresh must not silently drop queued work.** It surfaces as
 *     `auth-not-configured`/`unauthenticated`, the outbox item stays `queued`, and the technician is
 *     asked to sign in again. Attempts are NOT consumed by an auth failure — see `sync.ts`.
 *     → `src/auth/` imports `outbox.ts` **nowhere**, and `getAccessToken()` returning `null` makes
 *       `api.ts` answer `unauthenticated` *without sending*, which `sync.ts` turns into a
 *       `releaseClaim` that decrements `attempts`. The 10-hour refresh window expiring during a shift
 *       out of coverage is the expected case, and it is a named state with its own words, not a
 *       destructive sign-out.
 */

export interface TokenProvider {
  /**
   * Discriminates a real provider from the stub. `api.ts` refuses to send a request when this is
   * `'stub'`, which is the mechanism that stops the stub from ever looking like a session.
   */
  readonly kind: 'stub' | 'oidc-pkce';
  /**
   * A bearer access token, or `null` when there is no usable session.
   *
   * Must be safe to call concurrently and must coalesce refreshes (see requirement 3 above).
   */
  getAccessToken(): Promise<string | null>;
}

let warned = false;

/**
 * The development stub. Returns no token and announces itself.
 *
 * Do not "temporarily" make this return a hard-coded token. A token that works is a token that gets
 * committed, and this project has already had one fabricated attestation in a config file.
 */
export const devStubTokenProvider: TokenProvider = {
  kind: 'stub',
  getAccessToken(): Promise<string | null> {
    if (!warned) {
      warned = true;
      // eslint-disable-next-line no-console -- the whole purpose of this line is to be visible.
      console.warn(
        '[lockercustody] AUTH IS A STUB. No token is issued and no authenticated request will be ' +
          'sent. OIDC PKCE against IdPrism is not built (blocked on the Federate service profile, ' +
          'OQ-5). See src/core/auth.ts.',
      );
    }
    return Promise.resolve(null);
  },
};

/**
 * The provider the app uses. A single mutable module-level binding, set once at startup.
 *
 * A setter rather than a constructor argument threaded through every call site, because the outbox
 * flusher runs from timers and `online` events that have no React context to read from. Guarded
 * against a second call so a real provider cannot be silently replaced by a stub later in the
 * session.
 */
let active: TokenProvider = devStubTokenProvider;
let configured = false;

export function setTokenProvider(provider: TokenProvider): void {
  if (configured) {
    throw new Error('token provider already configured');
  }
  configured = true;
  active = provider;
}

export function tokenProvider(): TokenProvider {
  return active;
}

/** Test-only reset. Not exported through any barrel the app imports. */
export function resetTokenProviderForTests(): void {
  active = devStubTokenProvider;
  configured = false;
  warned = false;
}
